/**
 * useWhisperDictation
 *
 * React hook for voice dictation using Azure Whisper via the backend
 * transcription endpoint.  Records microphone audio with the existing
 * AudioCaptureManager, buffers PCM16 chunks, and sends WAV blobs to
 * the server for transcription.
 *
 * Supports two modes (controlled by VITE_DICTATION_MODE env var):
 *
 * 1. "batch" (default):
 *    - Record audio, show waveform animation
 *    - User confirms → send entire audio at once → render full text
 *    - Only 1 Whisper request per dictation session
 *    - 2-minute auto-confirm safety cap
 *
 * 2. "ghost" (legacy periodic flush):
 *    - Every PERIODIC_FLUSH_MS, send buffered audio for live ghost-text
 *    - Each transcription result appended progressively
 *    - Higher request volume (may hit rate limits on low-quota tiers)
 *
 * Works on ALL modern browsers — no Web Speech API or language-pack
 * dependency.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AudioCaptureManager } from '../utils/audio/audioCaptureManager';
import { pcm16ToWavBlob, downsample24to16 } from '../utils/audio/audioUtils';
import { dictationService } from '../utils/voice/dictation.service';
import { checkMicrophonePermission } from '../utils/audio/microphonePermission';
import logger from '../utils/logger/logger';

// ─── Configuration ───────────────────────────────────────────────────────────

/** Dictation mode: "batch" or "ghost" (legacy periodic flush). */
const _rawDictationMode = import.meta.env.VITE_DICTATION_MODE?.trim().toLowerCase();
const DICTATION_MODE: 'batch' | 'ghost' =
  _rawDictationMode === 'ghost' ? 'ghost' : 'batch';

/** Sample rate of AudioCaptureManager / AudioWorklet. */
const CAPTURE_SAMPLE_RATE = 24000;
/** Sample rate sent to Whisper (it resamples to 16 kHz internally anyway). */
const WHISPER_SAMPLE_RATE = 16000;
/** How often to send audio to Whisper for live interim results in ghost mode (ms).
 *  Set to 21s to stay under Azure S0 tier 3 RPM quota. Revert to 3000 once quota is 100 RPM. */
const PERIODIC_FLUSH_MS = 21000;
/** Minimum samples required before sending a chunk in ghost mode (~0.8s of audio at capture rate). */
const MIN_SAMPLES_TO_FLUSH = CAPTURE_SAMPLE_RATE * 0.8;
/** Audio level (0-1) below which we consider "silence". */
const SILENCE_LEVEL = 0.01;
/** Maximum recording duration in batch mode (ms). Auto-confirms after this. */
const MAX_RECORDING_MS = 120000; // 2 minutes

/** RMS threshold — chunks below this are considered silence and skipped. */
const RMS_SILENCE_THRESHOLD = 200;

/** Compute RMS (root-mean-square) energy of PCM16 samples. */
function computeRMS(samples: Int16Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) {
    sum += samples[i] * samples[i];
  }
  return Math.sqrt(sum / samples.length);
}

// ─── Interface ───────────────────────────────────────────────────────────────

export interface UseWhisperDictationReturn {
  /** Whether the microphone is actively recording. */
  isListening: boolean;
  /** Accumulated transcript across all chunks so far. */
  transcript: string;
  /** Whether a Whisper request is in flight. */
  isTranscribing: boolean;
  /** Whether the dictation service is booting up (mic initializing). */
  isStarting: boolean;
  /** Human-readable error, or null. */
  error: string | null;
  /** Current audio level (0.0–1.0) for waveform visualization. */
  audioLevel: number;
  /** Frequency spectrum data for detailed waveform (Uint8Array, 0-255 per bin). */
  frequencyData: Uint8Array | undefined;
  /** Start recording from the microphone. */
  startListening: () => Promise<void>;
  /** Stop recording and send the final chunk (ghost mode) or confirm+send (batch mode). */
  stopListening: () => Promise<void>;
  /** Confirm dictation — stop recording and send audio for transcription (batch mode). */
  confirmDictation: () => Promise<void>;
  /** Cancel dictation — stop recording and discard audio without sending (batch mode). */
  cancelDictation: () => void;
  /** Clear the accumulated transcript. */
  resetTranscript: () => void;
  /** Current dictation mode. */
  mode: 'batch' | 'ghost';
}

// ─── Hook Implementation ─────────────────────────────────────────────────────

export function useWhisperDictation(): UseWhisperDictationReturn {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [frequencyData, setFrequencyData] = useState<Uint8Array | undefined>(undefined);

  const captureManagerRef = useRef<AudioCaptureManager | null>(null);
  const audioBufferRef = useRef<Int16Array[]>([]);
  const periodicTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoConfirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isListeningRef = useRef(false);
  const isFlushingRef = useRef(false);
  const hasSpeechRef = useRef(false);
  /** Timestamp (ms) until which flushes are paused due to rate limiting (ghost mode only). */
  const rateLimitUntilRef = useRef<number>(0);

  const clearTimers = useCallback(() => {
    if (periodicTimerRef.current) {
      clearInterval(periodicTimerRef.current);
      periodicTimerRef.current = null;
    }
    if (autoConfirmTimerRef.current) {
      clearTimeout(autoConfirmTimerRef.current);
      autoConfirmTimerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
      captureManagerRef.current?.stop();
    };
  }, [clearTimers]);

  // ─── Ghost Mode: Periodic Flush ─────────────────────────────────────────

  /** Convert buffered PCM16 chunks into a WAV blob and send to Whisper (ghost mode). */
  const flushBuffer = useCallback(async () => {
    if (DICTATION_MODE !== 'ghost') return;

    // Prevent concurrent flushes
    if (isFlushingRef.current) return;

    // If rate-limited, keep buffering audio but skip sending
    if (Date.now() < rateLimitUntilRef.current) {
      logger.log('[useWhisperDictation] Rate-limited, skipping flush (audio still buffering)');
      return;
    }

    const chunks = audioBufferRef.current;
    if (chunks.length === 0) return;

    // Merge all chunks into a single Int16Array
    const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
    if (totalLength < MIN_SAMPLES_TO_FLUSH) return; // Too short, skip

    const merged = new Int16Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    // Clear the buffer for the next segment
    audioBufferRef.current = [];

    // Safeguard: WAV format uses 32-bit size fields, so data > ~4GB is invalid
    if (merged.length * 2 > 0xFFFFFFFF) {
      throw new Error('PCM data exceeds WAV format limit (~4GB)');
    }

    // Skip silent chunks
    const rms = computeRMS(merged);
    if (rms < RMS_SILENCE_THRESHOLD) {
      logger.log(`[useWhisperDictation] Skipping silent chunk (RMS=${rms.toFixed(0)})`);
      return;
    }

    // Downsample 24 kHz → 16 kHz
    const downsampled = downsample24to16(merged);
    const wavBlob = pcm16ToWavBlob(downsampled, WHISPER_SAMPLE_RATE);
    logger.log(`[useWhisperDictation] Flushing ${downsampled.length} samples (${wavBlob.size} bytes WAV, RMS=${rms.toFixed(0)})`);

    isFlushingRef.current = true;
    try {
      const text = await dictationService.transcribe(wavBlob);
      if (text.trim()) {
        setTranscript((prev) => (prev ? prev + ' ' + text.trim() : text.trim()));
      }
    } catch (err: any) {
      if (err.status === 429) {
        const backoffSec = err.retryAfterSeconds || 60;
        rateLimitUntilRef.current = Date.now() + backoffSec * 1000;
        logger.warn(`[useWhisperDictation] Rate limited — pausing flushes for ${backoffSec}s (audio continues buffering)`);
      } else {
        logger.warn('[useWhisperDictation] Periodic transcription error (non-fatal):', err.message);
      }
    } finally {
      isFlushingRef.current = false;
    }
  }, []);

  // ─── Shared: Send All Buffered Audio ────────────────────────────────────

  /** Send all buffered audio to Whisper (used on stop/confirm). */
  const sendBufferedAudio = useCallback(async () => {
    // Wait for any in-progress flush to complete (ghost mode)
    while (isFlushingRef.current) {
      await new Promise((r) => setTimeout(r, 50));
    }

    // In ghost mode, respect rate-limit backoff
    if (DICTATION_MODE === 'ghost' && Date.now() < rateLimitUntilRef.current) {
      logger.warn('[useWhisperDictation] Rate-limited — skipping final flush');
      audioBufferRef.current = [];
      return;
    }

    const chunks = audioBufferRef.current;
    if (chunks.length === 0) return;

    const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
    if (totalLength === 0) return;

    const merged = new Int16Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    audioBufferRef.current = [];

    // Safeguard: WAV format uses 32-bit size fields
    if (merged.length * 2 > 0xFFFFFFFF) {
      throw new Error('PCM data exceeds WAV format limit (~4GB)');
    }

    // Skip if pure silence
    const rms = computeRMS(merged);
    if (rms < RMS_SILENCE_THRESHOLD) {
      logger.log(`[useWhisperDictation] Skipping silent final chunk (RMS=${rms.toFixed(0)})`);
      return;
    }

    // Downsample 24 kHz → 16 kHz
    const downsampled = downsample24to16(merged);
    const wavBlob = pcm16ToWavBlob(downsampled, WHISPER_SAMPLE_RATE);
    logger.log(`[useWhisperDictation] Sending ${downsampled.length} samples (${wavBlob.size} bytes WAV, RMS=${rms.toFixed(0)})`);

    isFlushingRef.current = true;
    setIsTranscribing(true);
    try {
      const text = await dictationService.transcribe(wavBlob);
      if (text.trim()) {
        setTranscript((prev) => (prev ? prev + ' ' + text.trim() : text.trim()));
      }
    } catch (err: any) {
      logger.error('[useWhisperDictation] Transcription error:', err);
      setError(err.message || 'Transcription failed');
    } finally {
      isFlushingRef.current = false;
      setIsTranscribing(false);
    }
  }, []);

  // ─── Start Listening ────────────────────────────────────────────────────

  const startListening = useCallback(async () => {
    setIsStarting(true);
    setError(null);
    setAudioLevel(0);
    setFrequencyData(undefined);

    // Check permission first
    const permState = await checkMicrophonePermission();
    if (permState === 'denied') {
      setError('Microphone access denied. Please enable microphone permissions in your browser settings.');
      setIsStarting(false);
      return;
    }

    const manager = new AudioCaptureManager();
    captureManagerRef.current = manager;
    audioBufferRef.current = [];
    hasSpeechRef.current = false;

    try {
      await manager.start({
        onAudioData: (base64Audio: string) => {
          // Decode base64 back to Int16Array and buffer it
          const binaryStr = atob(base64Audio);
          const bytes = new Uint8Array(binaryStr.length);
          for (let i = 0; i < binaryStr.length; i++) {
            bytes[i] = binaryStr.charCodeAt(i);
          }
          const samples = new Int16Array(bytes.buffer);
          audioBufferRef.current.push(samples);
        },
        onAudioLevel: (level: number) => {
          // Track if user has spoken at all
          if (level > SILENCE_LEVEL) {
            hasSpeechRef.current = true;
          }
          // Expose audio level for waveform visualization
          setAudioLevel(level);
        },
        onFrequencyData: (data: Uint8Array) => {
          // Expose frequency data for detailed waveform visualization
          setFrequencyData(data);
        },
        onError: (err: Error) => {
          logger.error('[useWhisperDictation] Audio capture error:', err);
          setError(err.message);
          setIsListening(false);
          isListeningRef.current = false;
        },
        onNoAudioDetected: () => {
          logger.warn('[useWhisperDictation] No audio detected from microphone');
        },
      });

      setIsStarting(false);
      setIsListening(true);
      isListeningRef.current = true;

      if (DICTATION_MODE === 'ghost') {
        // Ghost mode: start periodic flushing for live ghost-text updates
        periodicTimerRef.current = setInterval(() => {
          if (isListeningRef.current) {
            flushBuffer();
          }
        }, PERIODIC_FLUSH_MS);
      } else {
        // Batch mode: start 2-minute auto-confirm timer
        autoConfirmTimerRef.current = setTimeout(() => {
          logger.log('[useWhisperDictation] Max recording duration reached (2 min), auto-confirming');
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          confirmDictationInternal();
        }, MAX_RECORDING_MS);
      }

    } catch (err: any) {
      logger.error('[useWhisperDictation] Failed to start listening:', err);
      setError(err.message || 'Failed to start microphone');
      setIsStarting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushBuffer]);

  // ─── Stop / Confirm / Cancel ────────────────────────────────────────────

  /** Internal confirm logic (shared by confirmDictation and auto-confirm timer). */
  const confirmDictationInternal = useCallback(async () => {
    clearTimers();
    isListeningRef.current = false;
    setIsListening(false);
    setAudioLevel(0);
    setFrequencyData(undefined);

    // Stop audio capture
    captureManagerRef.current?.stop();
    captureManagerRef.current = null;

    // Send all buffered audio
    await sendBufferedAudio();
  }, [clearTimers, sendBufferedAudio]);

  /** Stop recording and send the final chunk (ghost mode compatibility / legacy). */
  const stopListening = useCallback(async () => {
    await confirmDictationInternal();
  }, [confirmDictationInternal]);

  /** Confirm dictation — stop recording and send audio for transcription (batch mode). */
  const confirmDictation = useCallback(async () => {
    await confirmDictationInternal();
  }, [confirmDictationInternal]);

  /** Cancel dictation — stop recording and discard audio without sending. */
  const cancelDictation = useCallback(() => {
    clearTimers();
    isListeningRef.current = false;
    setIsListening(false);
    setAudioLevel(0);
    setFrequencyData(undefined);

    // Stop audio capture
    captureManagerRef.current?.stop();
    captureManagerRef.current = null;

    // Discard all buffered audio — no API call
    audioBufferRef.current = [];

    logger.log('[useWhisperDictation] Dictation cancelled — audio discarded');
  }, [clearTimers]);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    setError(null);
  }, []);

  return {
    isListening,
    transcript,
    isTranscribing,
    isStarting,
    error,
    audioLevel,
    frequencyData,
    startListening,
    stopListening,
    confirmDictation,
    cancelDictation,
    resetTranscript,
    mode: DICTATION_MODE,
  };
}
